import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import {
  Building2, MapPin, Phone, Globe, Star, Clock,
  Calendar, ChevronRight, Image as ImageIcon, FileText, User,
  ShieldCheck, Stethoscope, Navigation, FlaskConical, X, ChevronLeft,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import { formatDoctorName } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Prise de rendez-vous en ligne pour un laboratoire (page publique) : choix
// de l'acte, puis d'un creneau reellement disponible (meme moteur que cote
// etablissement, routes/agenda.py), puis coordonnees du patient. Extension
// demandee separement du cahier des charges labo, qui ne decrit la creation
// de rendez-vous que cote personnel (§6.3/§8).
const LabBookingModal = ({ clinic, initialAct, onClose }) => {
  const [step, setStep] = useState(initialAct ? 'slot' : 'act');
  const [acts, setActs] = useState([]);
  const [actsLoading, setActsLoading] = useState(!initialAct);
  const [selectedAct, setSelectedAct] = useState(initialAct || null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedTime, setSelectedTime] = useState('');
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', email: '', date_of_birth: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (initialAct) return;
    axios.get(`${API_URL}/api/clinics/${clinic.slug}/lab-acts`)
      .then((r) => setActs(r.data.act_types || []))
      .catch(() => setError("Impossible de charger la liste des analyses."))
      .finally(() => setActsLoading(false));
  }, [clinic.slug, initialAct]);

  useEffect(() => {
    if (step !== 'slot' || !selectedAct) return;
    setSlotsLoading(true); setSelectedTime(''); setError('');
    axios.get(`${API_URL}/api/clinics/${clinic.slug}/lab-slots`, { params: { date, act_type_id: selectedAct.act_type_id } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setError("Impossible de charger les creneaux disponibles."))
      .finally(() => setSlotsLoading(false));
  }, [step, selectedAct, date, clinic.slug]);

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.phone.trim()) {
      setError('Nom, prenom et telephone sont requis'); return;
    }
    setSubmitting(true); setError('');
    try {
      const r = await axios.post(`${API_URL}/api/clinics/${clinic.slug}/lab-appointments`, {
        act_type_id: selectedAct.act_type_id, date, time: selectedTime,
        first_name: form.first_name.trim(), last_name: form.last_name.trim(),
        phone: form.phone.trim(), email: form.email.trim() || null,
        date_of_birth: form.date_of_birth || null,
      });
      setConfirmation(r.data.appointment);
      setStep('success');
    } catch (e) {
      setError(e.response?.data?.detail || "Ce creneau n'est plus disponible, merci d'en choisir un autre.");
      if (e.response?.status === 409 || e.response?.status === 400) setStep('slot');
    } finally {
      setSubmitting(false);
    }
  };

  const minDate = new Date().toISOString().slice(0, 10);
  const maxDate = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[#E2E8F0] px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-2">
            {step !== 'act' && step !== 'success' && (
              <button onClick={() => setStep(step === 'info' ? 'slot' : 'act')} className="text-[#64748B] hover:text-[#0F172A]">
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <h3 className="font-bold text-[#0F172A]">Prendre rendez-vous</h3>
          </div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5">
          {error && <div className="bg-[#FCEBEB] border border-[#F7C1C1] text-[#A32D2D] rounded-lg px-3 py-2 text-sm mb-4">{error}</div>}

          {step === 'act' && (
            <div>
              <p className="text-sm text-[#64748B] mb-3">Choisissez l'analyse souhaitee :</p>
              {actsLoading ? (
                <div className="text-center py-8 text-[#94A3B8] text-sm">Chargement...</div>
              ) : acts.length === 0 ? (
                <div className="text-center py-8 text-[#94A3B8] text-sm">Aucune analyse disponible en ligne pour le moment. Appelez le laboratoire directement.</div>
              ) : (
                <div className="space-y-2">
                  {acts.map((a) => (
                    <button key={a.act_type_id} onClick={() => { setSelectedAct(a); setStep('slot'); }}
                      className="w-full text-left border border-[#E2E8F0] rounded-xl p-3 hover:border-[#0596DE] hover:bg-[#F5FAFF] transition">
                      <div className="font-medium text-[#0F172A]">{a.name}</div>
                      {a.preparation && <div className="text-xs text-[#64748B] mt-0.5">{a.preparation}</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 'slot' && selectedAct && (
            <div>
              <div className="text-sm font-medium text-[#0F172A] mb-1">{selectedAct.name}</div>
              {selectedAct.preparation && <div className="text-xs text-[#B45309] bg-[#FEF3C7] rounded-lg px-3 py-2 mb-3">⚠ {selectedAct.preparation}</div>}
              <label className="text-xs text-[#64748B] block mb-1">Date</label>
              <input type="date" value={date} min={minDate} max={maxDate}
                onChange={(e) => setDate(e.target.value)}
                className="w-full border border-[#CBD5E1] rounded-lg px-3 py-2 text-sm mb-4" />
              {slotsLoading ? (
                <div className="text-center py-6 text-[#94A3B8] text-sm">Recherche des creneaux...</div>
              ) : slots.length === 0 ? (
                <div className="text-center py-6 text-[#94A3B8] text-sm">Aucun creneau disponible ce jour-la. Essayez une autre date.</div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {slots.map((t) => (
                    <button key={t} onClick={() => { setSelectedTime(t); setStep('info'); }}
                      className="border border-[#E2E8F0] rounded-lg py-2 text-sm font-medium text-[#0596DE] hover:bg-[#E6F1FB] transition">{t}</button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 'info' && (
            <div>
              <div className="bg-[#F8FAFC] rounded-lg px-3 py-2 text-sm text-[#334155] mb-4">
                {selectedAct.name} · {new Date(date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} a {selectedTime}
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input placeholder="Prenom *" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="border border-[#CBD5E1] rounded-lg px-3 py-2 text-sm" />
                  <input placeholder="Nom *" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="border border-[#CBD5E1] rounded-lg px-3 py-2 text-sm" />
                </div>
                <input placeholder="Telephone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-[#CBD5E1] rounded-lg px-3 py-2 text-sm" />
                <input placeholder="Email (optionnel, pour recevoir la confirmation)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-[#CBD5E1] rounded-lg px-3 py-2 text-sm" />
                <div>
                  <label className="text-xs text-[#64748B] block mb-1">Date de naissance (optionnel)</label>
                  <input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="w-full border border-[#CBD5E1] rounded-lg px-3 py-2 text-sm" />
                </div>
                <Button onClick={submit} disabled={submitting} className="w-full bg-[#0596DE] hover:bg-[#047CBA] rounded-xl mt-2">
                  {submitting ? 'Confirmation...' : 'Confirmer le rendez-vous'}
                </Button>
              </div>
            </div>
          )}

          {step === 'success' && confirmation && (
            <div className="text-center py-4">
              <div className="w-14 h-14 bg-[#DCFCE7] rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-7 h-7 text-[#166534]" />
              </div>
              <h4 className="font-bold text-[#0F172A] text-lg mb-1">Rendez-vous confirme !</h4>
              <p className="text-sm text-[#64748B] mb-4">
                {confirmation.act_type_name} le <strong>{new Date(confirmation.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</strong> a <strong>{confirmation.time}</strong>
              </p>
              <div className="bg-[#F8FAFC] rounded-xl p-4 text-left text-sm text-[#475569] space-y-1">
                <div className="font-semibold text-[#0F172A]">{confirmation.clinic_name}</div>
                {confirmation.clinic_address && <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />{confirmation.clinic_address}</div>}
                {confirmation.clinic_phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 flex-shrink-0" />{confirmation.clinic_phone}</div>}
              </div>
              {form.email && <p className="text-xs text-[#94A3B8] mt-3">Une confirmation a ete envoyee a {form.email}.</p>}
              <Button onClick={onClose} className="w-full bg-[#0F172A] hover:bg-[#1E293B] rounded-xl mt-5">Fermer</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ClinicProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('about');
  const [availability, setAvailability] = useState({});
  const [labActs, setLabActs] = useState([]);
  const [showBooking, setShowBooking] = useState(false);
  const [bookingAct, setBookingAct] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/clinics/${slug}`);
        setData(res.data);
        if (res.data?.clinic?.establishment_type === 'hospital') {
          axios.get(`${API_URL}/api/clinics/${slug}/availability`)
            .then((a) => setAvailability(a.data || {}))
            .catch(() => {});
        } else if (res.data?.clinic?.establishment_type === 'laboratory') {
          axios.get(`${API_URL}/api/clinics/${slug}/lab-acts`)
            .then((a) => setLabActs(a.data?.act_types || []))
            .catch(() => {});
        }
      } catch (e) {
        // 404 ou erreur
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin w-12 h-12 border-4 border-[#0596DE] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data || !data.clinic) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <Building2 className="w-16 h-16 text-[#94A3B8] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#0F172A] mb-2">Clinique introuvable</h1>
          <p className="text-[#64748B] mb-6">Cette clinique n'existe pas ou n'est plus active.</p>
          <Button onClick={() => navigate('/')} className="rounded-full">Retour à l'accueil</Button>
        </div>
      </div>
    );
  }

  // Correctif Nadjib (2026-09-07) : une pharmacie avec un compte "Pro"
  // (admin_create_clinic ou register_pharmacy_public) atterrissait ici
  // avec le template generique hopital/clinique (onglets Medecins/
  // Realisations/Articles/Avis, "Prendre rendez-vous"), au lieu du
  // template dedie pharmacie (adresse/horaires/telephone Google, badge
  // de garde) qui vit a /pharmacie/:slug (PharmacyProfile.js). Redirige
  // plutot que de dupliquer ce rendu ici — corrige aussi du meme coup le
  // lien "Voir page publique" du panneau admin (AdminClinics.js), qui
  // pointe toujours vers /clinic/:slug quel que soit le type.
  if (data.clinic.establishment_type === 'pharmacy') {
    return <Navigate to={`/pharmacie/${slug}`} replace />;
  }

  const { clinic, doctors, portfolio, articles, reviews, services } = data;
  const isHospital = clinic.establishment_type === 'hospital';
  const isLab = clinic.establishment_type === 'laboratory';
  const v = clinic.vitrine || {};
  const st = v.stats || {};
  const hasStats = [st.patients, st.interventions, st.urgences, st.consultations].some((x) => x != null && x !== '');
  const conventions = v.conventions || [];
  const mapQuery = encodeURIComponent(`${clinic.name}, ${clinic.address || ''}, ${clinic.daira || ''}, Algerie`);
  const avgRating = reviews?.length
    ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1)
    : null;

  const handleAssign = async (serviceId) => {
    try {
      const res = await axios.get(`${API_URL}/api/clinics/${slug}/services/${serviceId}/assign`);
      navigate(`/doctor/${res.data.doctor_id}`);
    } catch (e) {
      alert(e.response?.data?.detail || "Aucune disponibilité dans ce service pour le moment");
    }
  };

  const renderDoctorCard = (d) => (
    <div key={d.doctor_id} className="bg-white rounded-2xl border border-[#E6EDF3] p-5 hover:shadow-lg hover:border-[#CBE3F5] transition-all duration-200">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0596DE] to-[#047CBA] flex items-center justify-center text-white font-bold text-xl flex-shrink-0 overflow-hidden ring-2 ring-[#E6F1FB]">
          {d.photo_url ? <img src={d.photo_url} alt={d.name} className="w-full h-full object-cover" /> : (d.name?.charAt(0)?.toUpperCase() || 'D')}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-[#0F172A] truncate">{formatDoctorName(d.name)}</h3>
          <p className="text-sm text-[#0596DE]">{d.specialty}</p>
          {d.fees && <p className="text-xs text-[#64748B] mt-1">À partir de {d.fees} DA</p>}
        </div>
      </div>
      {availability[d.doctor_id]?.slots?.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-[#3B6D11] mb-1.5 font-medium">{availability[d.doctor_id].label}</p>
          <div className="flex flex-wrap gap-1.5">
            {availability[d.doctor_id].slots.slice(0, 3).map((t) => (
              <button key={t} onClick={() => navigate(`/doctor/${d.doctor_id}`)} className="text-xs bg-[#E6F1FB] text-[#185FA5] rounded-lg px-2.5 py-1.5 hover:bg-[#D3E6F8] transition">{t}</button>
            ))}
            <button onClick={() => navigate(`/doctor/${d.doctor_id}`)} className="text-xs bg-[#F1F5F9] text-[#475569] rounded-lg px-2.5 py-1.5 hover:bg-[#E2E8F0] transition">Voir +</button>
          </div>
        </div>
      )}
      <Button onClick={() => navigate(`/doctor/${d.doctor_id}`)} className="w-full bg-[#0596DE] hover:bg-[#047CBA] rounded-xl">
        <Calendar className="w-4 h-4 mr-2" /> Prendre RDV
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5F8FC]">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A2540]">
        {clinic.cover_url ? (
          <div className="absolute inset-0">
            <img src={clinic.cover_url} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#0A2540] via-[#0A2540]/80 to-[#0A2540]/30" />
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#0596DE] via-[#047CBA] to-[#0A2540]" />
        )}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 md:pt-20 md:pb-20">
          <div className="flex flex-col md:flex-row md:items-end gap-7 text-white">
            <div className="w-20 h-20 md:w-28 md:h-28 rounded-2xl bg-white shadow-2xl ring-1 ring-white/20 flex items-center justify-center overflow-hidden flex-shrink-0">
              {clinic.logo_url ? (
                <img src={clinic.logo_url} alt={clinic.name} className="w-full h-full object-cover" />
              ) : (
                <Building2 className="w-10 h-10 md:w-14 md:h-14 text-[#0596DE]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/15 backdrop-blur rounded-full text-[11px] uppercase tracking-[0.18em] font-medium mb-4 ring-1 ring-white/20">
                {isLab ? <FlaskConical className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                {isLab ? 'Laboratoire d\'analyses médicales' : (clinic.clinic_type || (isHospital ? 'Établissement hospitalier' : 'Établissement de santé'))}
              </div>
              <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4 leading-[1.05]">{clinic.name}</h1>
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/85">
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${mapQuery}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 hover:text-white transition"
                >
                  <MapPin className="w-4 h-4" />
                  {clinic.daira ? `${clinic.daira}, ` : ''}{clinic.address}
                </a>
                {avgRating && (
                  <span className="flex items-center gap-1.5">
                    <Star className="w-4 h-4 fill-yellow-300 text-yellow-300" />
                    {avgRating} ({reviews.length} avis)
                  </span>
                )}
                {doctors?.length > 0 && (
                  <span className="flex items-center gap-1.5">
                    <Stethoscope className="w-4 h-4" /> {doctors.length} médecin(s)
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-2.5 w-full md:w-auto">
              <button
                onClick={() => {
                  if (isLab) { setBookingAct(null); setShowBooking(true); return; }
                  setActiveSection('doctors');
                  document.getElementById('doctors')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="bg-white text-[#0A2540] px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-white/90 transition shadow-lg"
              >
                <Calendar className="w-5 h-5" /> Prendre rendez-vous
              </button>
              {clinic.phone && (
                <a href={`tel:${clinic.phone}`} className="bg-white/10 border border-white/30 text-white px-5 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-white/20 transition">
                  <Phone className="w-4 h-4" /> {clinic.phone}
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* BANDEAU CHIFFRES CLES (chevauche le hero) */}
      {isHospital && hasStats && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10">
          <div className="bg-white rounded-2xl shadow-xl ring-1 ring-[#E6EDF3] overflow-hidden">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#EEF3F8]">
              {[['Patients / an', st.patients], ['Interventions / an', st.interventions], ['Passages urgences / an', st.urgences], ['Consultations / an', st.consultations]].map(([label, val]) => (
                <div key={label} className="bg-white p-5 md:p-6 text-center">
                  <div className="text-2xl md:text-3xl font-bold text-[#0A2540]">{val != null && val !== '' ? `+${Number(val).toLocaleString('fr-FR')}` : '—'}</div>
                  <div className="text-xs text-[#64748B] mt-1">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* NAVIGATION ANCRÉE */}
      <div className="sticky top-16 z-10 bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-1 overflow-x-auto">
          {[
            { id: 'about', label: 'À propos', icon: Building2 },
            ...(isLab ? (labActs.length > 0 ? [{ id: 'actes', label: `Nos analyses (${labActs.length})`, icon: FlaskConical }] : []) : [
              { id: 'doctors', label: isHospital ? 'Services & médecins' : `Médecins (${doctors?.length || 0})`, icon: User },
            ]),
            ...(!isHospital && !isLab ? [
              { id: 'portfolio', label: `Réalisations (${portfolio?.length || 0})`, icon: ImageIcon },
              { id: 'articles', label: `Articles (${articles?.length || 0})`, icon: FileText },
            ] : []),
            ...(((reviews?.length || 0) > 0 || (!isHospital && !isLab)) ? [{ id: 'reviews', label: `Avis (${reviews?.length || 0})`, icon: Star }] : []),
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveSection(tab.id);
                document.getElementById(tab.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeSection === tab.id
                  ? 'border-[#0596DE] text-[#0596DE]'
                  : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-12">
        {/* À PROPOS */}
        <section id="about" className="scroll-mt-32">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2 bg-white rounded-2xl border border-[#E2E8F0] p-6">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">{isLab ? 'À propos du laboratoire' : 'À propos de la clinique'}</h2>
              <p className="text-[#64748B] leading-relaxed mb-6">
                {clinic.description || (isLab ? `${clinic.name} vous accueille pour vos analyses médicales dans un cadre professionnel.` : `${clinic.name} vous accueille pour vos consultations dans un cadre professionnel et moderne.`)}
              </p>
              {clinic.specialties?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-3 text-sm">Spécialités proposées</h3>
                  <div className="flex flex-wrap gap-2">
                    {clinic.specialties.map((s, i) => (
                      <span key={i} className="px-3 py-1.5 bg-[#E0F2FE] text-[#0596DE] rounded-full text-sm font-medium">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 space-y-4">
              <h3 className="font-semibold text-[#0F172A]">Contact</h3>
              <div className="space-y-3 text-sm">
                
                  <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${clinic.name}, ${clinic.address}, ${clinic.daira || ''}, Algerie`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 hover:bg-[#F8FAFC] p-2 -m-2 rounded-lg transition group"
                >
                  <MapPin className="w-4 h-4 text-[#0596DE] mt-0.5 flex-shrink-0" />
                  <span className="text-[#64748B] group-hover:text-[#0596DE]">
                    {clinic.daira ? `${clinic.daira}, ` : ''}{clinic.address}
                    <span className="block text-xs text-[#0596DE] mt-1">📍 Voir sur Google Maps</span>
                  </span>
                </a>
                {clinic.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-[#0596DE] flex-shrink-0" />
                    <a href={`tel:${clinic.phone}`} className="text-[#0596DE] hover:underline">{clinic.phone}</a>
                  </div>
                )}

                {clinic.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="w-4 h-4 text-[#0596DE] flex-shrink-0" />
                    <a href={clinic.website} target="_blank" rel="noopener noreferrer" className="text-[#0596DE] hover:underline break-all">
                      {clinic.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
              </div>
              {isLab && (
                <Button onClick={() => { setBookingAct(null); setShowBooking(true); }} className="w-full bg-[#0596DE] hover:bg-[#047CBA] rounded-xl">
                  <Calendar className="w-4 h-4 mr-2" /> Prendre rendez-vous
                </Button>
              )}
            </div>
          </div>
        </section>

        {/* HORAIRES */}
        {(isHospital || isLab) && (v.hours_urgences || v.hours_consultations) && (
          <section className="scroll-mt-32">
            <h2 className="text-xl font-bold text-[#0F172A] mb-4">Horaires</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {v.hours_urgences && (
                <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
                  <div className="flex items-center gap-2 text-[#A32D2D] font-semibold mb-1"><Clock className="w-5 h-5" /> Urgences</div>
                  <p className="text-[#475569]">{v.hours_urgences}</p>
                </div>
              )}
              {v.hours_consultations && (
                <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
                  <div className="flex items-center gap-2 text-[#0596DE] font-semibold mb-1"><Clock className="w-5 h-5" /> {isLab ? "Horaires d'ouverture" : 'Consultations externes'}</div>
                  <p className="text-[#475569]">{v.hours_consultations}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* NOS ANALYSES (laboratoire) */}
        {isLab && (
          <section id="actes" className="scroll-mt-32">
            <h2 className="text-xl font-bold text-[#0F172A] mb-4">Nos analyses</h2>
            {labActs.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#E2E8F0] p-12 text-center">
                <FlaskConical className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
                <p className="text-[#64748B]">Le catalogue d'analyses n'est pas encore publié. Contactez le laboratoire par téléphone.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {labActs.map((a) => (
                  <div key={a.act_type_id} className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
                    <h3 className="font-semibold text-[#0F172A]">{a.name}</h3>
                    {a.preparation && <p className="text-xs text-[#B45309] bg-[#FEF3C7] rounded-lg px-2.5 py-1.5 mt-2">⚠ {a.preparation}</p>}
                    <Button onClick={() => { setBookingAct(a); setShowBooking(true); }} variant="outline" className="w-full mt-3 rounded-xl border-[#0596DE] text-[#0596DE] hover:bg-[#E6F4FB]">
                      <Calendar className="w-4 h-4 mr-2" /> Réserver
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {isHospital && clinic.booklet_url && (
          <section className="scroll-mt-32">
            <a href={clinic.booklet_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-white border border-[#E2E8F0] rounded-2xl px-5 py-4 hover:shadow-md transition text-[#0F172A]">
              <FileText className="w-5 h-5 text-[#0596DE]" /> Télécharger le livret d'accueil <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
            </a>
          </section>
        )}

        {!isLab && (
        <section id="doctors" className="scroll-mt-32">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-[#0F172A]">{isHospital ? 'Nos services et médecins' : 'Notre équipe médicale'}</h2>
          </div>
          {doctors?.length === 0 ? (
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-12 text-center">
              <User className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
              <p className="text-[#64748B]">Aucun médecin référencé pour le moment</p>
            </div>
          ) : isHospital && services?.length > 0 ? (
            <div className="space-y-8">
              {services.map((svc) => {
                const svcDoctors = doctors.filter((d) => d.service_id === svc.service_id);
                if (svcDoctors.length === 0) return null;
                return (
                  <div key={svc.service_id}>
                    {svc.photo_url ? (
                      <div className="relative w-full h-44 rounded-2xl overflow-hidden mb-4">
                        <img src={svc.photo_url} alt={svc.name} className="w-full h-full object-cover" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/85 via-[#0A2540]/20 to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 p-4 flex flex-wrap items-end justify-between gap-3">
                          <h3 className="text-xl font-bold text-white drop-shadow">{svc.name}</h3>
                          <Button onClick={() => handleAssign(svc.service_id)} className="rounded-full bg-white text-[#0A2540] hover:bg-white/90">
                            <Calendar className="w-4 h-4 mr-2" /> Prendre RDV
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-2 border-b-2 border-[#0596DE]/20">
                        <h3 className="text-lg font-bold text-[#0F172A] flex items-center gap-2"><span className="w-1.5 h-5 bg-[#0596DE] rounded-full" />{svc.name}</h3>
                        <Button onClick={() => handleAssign(svc.service_id)} variant="outline" className="rounded-full border-[#0596DE] text-[#0596DE] hover:bg-[#E6F4FB]">
                          <Calendar className="w-4 h-4 mr-2" /> Prendre RDV dans ce service
                        </Button>
                      </div>
                    )}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {svcDoctors.map(renderDoctorCard)}
                    </div>
                  </div>
                );
              })}
              {(() => {
                const orphans = doctors.filter((d) => !services.some((s) => s.service_id === d.service_id));
                if (orphans.length === 0) return null;
                return (
                  <div>
                    <h3 className="text-lg font-bold text-[#0F172A] mb-3 pb-2 border-b border-[#E2E8F0]">Autres médecins</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{orphans.map(renderDoctorCard)}</div>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {doctors.map(renderDoctorCard)}
            </div>
          )}
        </section>
        )}

        {/* PORTFOLIO */}
        {(isHospital || isLab) && conventions.length > 0 && (
          <section className="scroll-mt-32">
            <h2 className="text-xl font-bold text-[#0F172A] mb-4">Conventions & mutuelles</h2>
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5 flex flex-wrap gap-2">
              {conventions.map((c, i) => (
                <span key={i} className="text-sm bg-[#F1F5F9] text-[#334155] rounded-full px-3 py-1.5">{c}</span>
              ))}
            </div>
          </section>
        )}

        {(isHospital || isLab) && (
          <section className="scroll-mt-32">
            <h2 className="text-xl font-bold text-[#0F172A] mb-4">Localisation</h2>
            <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden">
              <iframe
                title="Carte"
                src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
                className="w-full"
                style={{ height: 320, border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="p-4 text-sm text-[#475569] flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#0596DE]" /> {clinic.address}{clinic.daira ? `, ${clinic.daira}` : ''}
              </div>
            </div>
          </section>
        )}

        {!isHospital && !isLab && (
        <section id="portfolio" className="scroll-mt-32">
          <h2 className="text-xl font-bold text-[#0F172A] mb-4">Nos réalisations</h2>
          {!portfolio?.length ? (
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-12 text-center">
              <ImageIcon className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
              <p className="text-[#64748B]">Aucune réalisation publiée pour le moment</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {portfolio.map((item) => (
                <div key={item.item_id} className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden">
                  <div className="grid grid-cols-2">
                    {item.before_url && (
                      <div className="relative aspect-square bg-[#F8FAFC]">
                        <img src={item.before_url} alt="Avant" className="w-full h-full object-cover" />
                        <span className="absolute top-2 left-2 px-2 py-0.5 bg-black/60 text-white text-xs rounded-full">Avant</span>
                      </div>
                    )}
                    {item.after_url && (
                      <div className="relative aspect-square bg-[#F8FAFC]">
                        <img src={item.after_url} alt="Après" className="w-full h-full object-cover" />
                        <span className="absolute top-2 left-2 px-2 py-0.5 bg-[#0596DE] text-white text-xs rounded-full">Après</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <span className="text-xs px-2 py-0.5 bg-[#E0F2FE] text-[#0596DE] rounded-full">{item.category}</span>
                    <h3 className="font-semibold text-[#0F172A] mt-2">{item.title}</h3>
                    {item.description && <p className="text-sm text-[#64748B] mt-1">{item.description}</p>}
                    {item.doctor_name && <p className="text-xs text-[#94A3B8] mt-2">Réalisé par {formatDoctorName(item.doctor_name)}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
        )}

        {/* ARTICLES */}
        {!isHospital && !isLab && (
        <section id="articles" className="scroll-mt-32">
          <h2 className="text-xl font-bold text-[#0F172A] mb-4">Articles & conseils</h2>
          {!articles?.length ? (
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-12 text-center">
              <FileText className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
              <p className="text-[#64748B]">Aucun article publié pour le moment</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {articles.map((a) => (
                <article key={a.article_id} onClick={() => navigate(`/article/${a.article_id}`)} className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden hover:shadow-md transition cursor-pointer">
                  {a.cover_url && (
                    <div className="aspect-video bg-[#F8FAFC]">
                      <img src={a.cover_url} alt={a.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-5">
                    <h3 className="font-semibold text-[#0F172A] mb-2 line-clamp-2">{a.title}</h3>
                    <p className="text-sm text-[#64748B] line-clamp-3">{a.content}</p>
                    <p className="text-xs text-[#94A3B8] mt-3">
                      {new Date(a.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
        )}

        {/* AVIS */}
        {((reviews?.length || 0) > 0 || (!isHospital && !isLab)) && (
        <section id="reviews" className="scroll-mt-32">
          <h2 className="text-xl font-bold text-[#0F172A] mb-4">Avis des patients</h2>
          {!reviews?.length ? (
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-12 text-center">
              <Star className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
              <p className="text-[#64748B]">Aucun avis pour le moment</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {reviews.slice(0, 6).map((r, i) => (
                <div key={i} className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
                  <div className="flex items-center gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-4 h-4 ${n <= r.rating ? 'fill-yellow-400 text-yellow-400' : 'text-[#E2E8F0]'}`} />
                    ))}
                  </div>
                  <p className="text-sm text-[#64748B]">{r.comment}</p>
                  <p className="text-xs text-[#94A3B8] mt-3">
                    {new Date(r.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>
        )}
      </div>

      {/* FOOTER */}
      <footer className="bg-[#0A2540] text-white py-10 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between gap-6 pb-8 border-b border-white/10">
            <div>
              <h3 className="font-bold text-lg">{clinic.name}</h3>
              <p className="text-sm text-white/60 mt-1 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {clinic.daira ? `${clinic.daira}, ` : ''}{clinic.address}</p>
              {clinic.phone && <p className="text-sm text-white/60 mt-1 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {clinic.phone}</p>}
            </div>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${mapQuery}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 self-start bg-white/10 hover:bg-white/20 transition px-5 py-2.5 rounded-full text-sm font-medium"
            >
              <Navigation className="w-4 h-4" /> Itinéraire
            </a>
          </div>
          <div className="pt-6 text-center">
            <span className="font-bold text-lg text-[#4FB6E8]">Tabbibak.dz</span>
            <p className="text-sm text-white/50 mt-1">© {new Date().getFullYear()} Tabbibak.dz — Prenez rendez-vous en ligne</p>
          </div>
        </div>
      </footer>

      {showBooking && (
        <LabBookingModal clinic={clinic} initialAct={bookingAct} onClose={() => { setShowBooking(false); setBookingAct(null); }} />
      )}
    </div>
  );
};

export default ClinicProfile;
